import React from 'react';
import { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import NS_SSO from '@rsos/constants/ns_sso';
import { fetchUser, loginWithProvider, sessionStart } from '@rsos/sinatra';
import { configureUserScope, captureException } from '@rsos/utils/sentry';
import { notifyError } from '@rsos/utils/toastUtils';
import {
  trackNSLoginForCSP,
  trackSessionStart,
} from '../../actions/authTrackings';
import rapidsosLogo from '../../assets/rapidsos_logo_light.png';
import { AppRedirecting } from '../Landing/Landing.styles';
import {
  LoginButton,
  LoginWrapper,
  LoginImage,
  StyledPage,
} from './NSLogin.styles';
import createLoginURI from './createLoginURI';
import parseQueryString from './parseQueryString';

export const LOGIN_URI = createLoginURI();
export const LOGIN_BUTTON_TEXT = 'Sign in with SSO';
export const LOGIN_ERROR_MESSAGE = `Login failed: We could not authenticate you using SSO. If this error persists, please reach out to your system admin.`;

class NSLoginUserError extends Error {
  constructor(message) {
    super(message);
    this.name = 'NSLoginUserError';
  }
}

class NSLoginAPIError extends Error {
  constructor(message) {
    super(message);
    this.name = 'NSLoginAPIError';
  }
}

const reportAPIError = (errorInstance, errorExtras = {}) => {
  captureException(errorInstance, errorExtras);
};

const NSLogin = () => {
  const dispatch = useDispatch();
  const isLoggedIn = useSelector(state => state.sinatra?.user?.isLoggedIn);

  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isOIDCSuccess, setIsOIDCSuccess] = useState(false);
  const [isOIDCFailure, setIsOIDCFailure] = useState(false);
  const [parsedArgs, setParsedArgs] = useState(null);
  const isExchangingOIDCCode = useRef(false);

  const doRedirect = useCallback(
    async ({ shouldStartSession = false } = {}) => {
      let user;
      try {
        user = await dispatch(fetchUser());
      } catch (error) {
        reportAPIError(error);
        setIsOIDCFailure(true);
        setIsOIDCSuccess(false);
        return false;
      }

      // While here, identify the user to Sentry.
      try {
        configureUserScope({
          email: user?.email,
          version: process.env.REACT_APP_FE_VERSION || '',
        });
      } catch (error) {
        captureException(error);
      }

      if (user?.currentRole?.application === 'central_station') {
        if (shouldStartSession) {
          // Business rule: NS SSO is allowed to enter only the CSP
          // central_station application, so do not start the portal timeout
          // clock until both checks are complete:
          //   1. Scorpius exchanged the OIDC code for an access token.
          //   2. fetchUser() proved the token belongs to a central_station user.
          //
          // sessionStart() stores Unix timestamps in seconds. By default it
          // gives the user a 172800s (48 hour) fixed wall-clock session, which
          // matches password login. The clock is not an inactivity timer, and
          // already-authenticated redirects call doRedirect() with
          // shouldStartSession=false so they keep their existing expiration.
          //
          // This ordering keeps malformed token responses, missing users, and
          // non-CSP users from getting a partial or extended portal session.
          dispatch(sessionStart());
        }
        try {
          trackSessionStart();
        } catch (error) {
          captureException(error);
        }
        try {
          trackNSLoginForCSP();
        } catch (error) {
          captureException(error);
        }
        window.location.assign('/central-station');
        return true;
      } else if (user?.currentRole?.application) {
        const error = new NSLoginUserError(
          'User is not a central_station user',
        );
        reportAPIError(error);
        setIsOIDCFailure(true);
        setIsOIDCSuccess(false);
        return false;
      } else if (user && !user?.currentRole?.application) {
        const error = new NSLoginUserError(
          'User missing currentRole.application',
        );
        reportAPIError(error);
        setIsOIDCFailure(true);
        setIsOIDCSuccess(false);
        return false;
      }
      const error = new NSLoginUserError('User response missing');
      reportAPIError(error);
      setIsOIDCFailure(true);
      setIsOIDCSuccess(false);
      return false;
    },
    [dispatch],
  );

  useEffect(() => {
    if (isLoggedIn && isRedirecting) {
      doRedirect();
      return;
    }
  }, [isLoggedIn, isRedirecting, doRedirect]);

  useEffect(() => {
    // NOTE: Only users that know of the /ns will navigate to the page.
    // This allows us to tag the user as an NS user (ns_sso in localStorage).
    // The tag is used to redirect the user back to the /ns page when their
    // central_station session expires or they logout.
    // Although NS users will be trained to login via the /ns page. If the user
    // manually changes the URL to visit the homepage (gets out of the ns flow),
    // the NS tag is removed from localStorage from the Landing component.
    // This is prevents the user from being redirected to the /ns page if they
    // ever login with RSOS credentials (QA will do this).
    // Search for NS_SSO to see exactly where it's used.
    localStorage.setItem(NS_SSO, true);

    // Check if the user is logged in. If so, redirect to central_station.
    if (isLoggedIn) {
      setIsRedirecting(true);
      return;
    }

    const parsedArgs = parseQueryString(window.location.search);
    const { code, state, error } = parsedArgs;
    setParsedArgs({ code, state, error });
    if (error) {
      setIsOIDCFailure(true);
    } else if (code && state) {
      setIsOIDCSuccess(true);
    } else if ((code && !state) || (!code && state)) {
      setIsOIDCFailure(true);
      reportAPIError(new NSLoginAPIError('Missing OIDC params'), {
        code,
        state,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isOIDCFailure) {
      notifyError(LOGIN_ERROR_MESSAGE, { autoClose: false });
    }
  }, [isOIDCFailure]);

  // When isOIDCSuccess is true, we need complete token exchange with Scorpius,
  // then fetch the user with the new access token, and finally redirect to the
  // central_station app after confirming the user has a central_station role.
  useEffect(() => {
    if (isOIDCSuccess) {
      const code = parsedArgs?.code;
      const state = parsedArgs?.state;

      if (!code || !state) {
        // Defensive guard for impossible-but-expensive state skew: the mount
        // effect sets parsedArgs and isOIDCSuccess together, but do not let a
        // future refactor turn a missing query param into a render-time throw.
        const error = new NSLoginAPIError('Missing OIDC params');
        reportAPIError(error, { code, state });
        setIsOIDCFailure(true);
        setIsOIDCSuccess(false);
        return;
      }

      (async () => {
        if (isExchangingOIDCCode.current) {
          return;
        }

        let tokens;
        try {
          isExchangingOIDCCode.current = true;
          const resp = await dispatch(loginWithProvider('ns', { code, state }));
          tokens = resp?.tokens;
        } catch {
          const error = new NSLoginAPIError('Token exchange failed');
          reportAPIError(error, { code, state });
          setIsOIDCFailure(true);
          setIsOIDCSuccess(false);
          return;
        } finally {
          isExchangingOIDCCode.current = false;
        }

        if (tokens?.token) {
          // Keep NS SSO behavior aligned with password login. doRedirect will
          // start the default 172800s (48 hour) session only after fetchUser
          // confirms this authenticated user can enter central_station.
          await doRedirect({ shouldStartSession: true });
        } else if (tokens) {
          const error = new NSLoginAPIError(
            'Token exchange returned no access token',
          );
          reportAPIError(error, { code, state });
          setIsOIDCFailure(true);
          setIsOIDCSuccess(false);
        } else {
          const error = new NSLoginAPIError(
            'Token exchange returned no response',
          );
          reportAPIError(error, { code, state });
          setIsOIDCFailure(true);
          setIsOIDCSuccess(false);
        }
      })();
    }
  }, [dispatch, isOIDCSuccess, parsedArgs, doRedirect]);

  return (
    <StyledPage>
      {isRedirecting ? (
        <AppRedirecting />
      ) : (
        <LoginWrapper>
          <LoginImage src={rapidsosLogo} />
          <LoginButton
            isLoadingProp={isOIDCSuccess}
            onClick={() => window.location.assign(LOGIN_URI)}
            dataName="ns-sso-button"
          >
            {LOGIN_BUTTON_TEXT}
          </LoginButton>
        </LoginWrapper>
      )}
    </StyledPage>
  );
};

export default NSLogin;
